<template>
  <div class="table-plus">
    <el-table
      ref="elTable"
      v-loading="loading"
      :element-loading-text="loadingText"
      :data="data"
      :height="tableHeight"
      :max-height="maxHeight"
      highlight-current-row
      v-bind="$attrs"
      v-on="$listeners"
    >
      <!-- 列（含多选/序号列）直接写在 TablePlus 标签内（原生 el-table-column） -->
      <slot></slot>
      <template v-slot:empty>
        <slot name="empty">
          <el-empty :image-size="80" :description="emptyText"></el-empty>
        </slot>
      </template>
    </el-table>
    <div v-if="hasPagination" class="table-plus__pagination">
      <el-pagination
        background
        :layout="paginationLayout"
        :page-sizes="pageSizes"
        :current-page="page"
        :page-size="pageSize"
        :total="total"
        @size-change="handleSizeChange"
        @current-change="handlePageChange"
      >
      </el-pagination>
    </div>
  </div>
</template>

<script>
/**
 * 通用表格组件 TablePlus
 *
 * 用法示例：
 * <TablePlus
 *   has-pagination
 *   :data="list"
 *   :loading="loading"
 *   :page="query.page"
 *   :page-size="query.pageSize"
 *   :total="total"
 *   @handleSizeChange="(size) => {}"
 *   @handleCurrentChange="(page) => {}"
 *   @selection-change="(rows) => {}"
 * >
 *   <el-table-column prop="name" label="姓名" show-overflow-tooltip />
 *   <el-table-column label="操作">
 *     <template slot-scope="{ row }">
 *       <el-button type="text" @click="edit(row)">编辑</el-button>
 *     </template>
 *   </el-table-column>
 * </TablePlus>
 *
 * 说明：
 * - 列直接在标签内写原生 el-table-column，不需要传 field；
 *   多选列、序号列也自行书写（type="selection" / type="index"）
 * - el-table 的所有 props（border/stripe/row-key/tree-props/lazy/load/span-method 等）
 *   和所有事件（sort-change/row-click/cell-click/selection-change 等）都会透传，直接写在标签上即可
 * - 分页事件：@handleSizeChange(每页条数) / @handleCurrentChange(页码)，
 *   与 VTable 保持一致，老页面只需替换组件引用
 * - 其余 el-table 事件（sort-change/row-click/cell-click/selection-change 等）原样透传
 * - 高度：默认自动撑满剩余视口（auto-height）；传了 height/max-height 则以传入值为准
 * - 空数据默认展示 el-empty，可用 <template v-slot:empty> 自定义
 */
export default {
  name: "TablePlus",
  // $attrs 全部交给 el-table，不要落到根节点上
  inheritAttrs: false,
  props: {
    data: { type: Array, default: () => [] },
    loading: { type: Boolean, default: false },
    loadingText: { type: String, default: "加载中" },
    emptyText: { type: String, default: "暂无数据" },
    // 分页
    hasPagination: { type: Boolean, default: false },
    page: { type: Number, default: 1 },
    pageSize: { type: Number, default: 10 },
    total: { type: Number, default: 0 },
    pageSizes: { type: Array, default: () => [10, 20, 50, 100, 150] },
    paginationLayout: {
      type: String,
      default: "total, sizes, prev, pager, next, jumper",
    },
    // 高度：传了 height / max-height 就不再自动计算
    height: { type: [String, Number], default: null },
    maxHeight: { type: [String, Number], default: null },
    autoHeight: { type: Boolean, default: true },
  },
  data() {
    return {
      // 自动计算的高度，mounted 后立即重算
      innerHeight: 500,
      resizeObserver: null,
    };
  },
  computed: {
    tableHeight() {
      if (this.height !== null && this.height !== undefined) return this.height;
      if (this.maxHeight !== null && this.maxHeight !== undefined) return null;
      return this.autoHeight ? this.innerHeight : null;
    },
  },
  watch: {
    data() {
      this.$nextTick(() => {
        this.resetTableHeight();
        this.doLayout();
      });
    },
  },
  mounted() {
    this.$nextTick(this.resetTableHeight);
    window.addEventListener("resize", this.resetTableHeight);
    // ResizeObserver：侧边栏折叠/容器尺寸变化也能重算
    if (
      typeof ResizeObserver !== "undefined" &&
      this.$refs.elTable &&
      this.$refs.elTable.$el
    ) {
      this.resizeObserver = new ResizeObserver(this.resetTableHeight);
      this.resizeObserver.observe(this.$refs.elTable.$el);
    }
  },
  beforeDestroy() {
    window.removeEventListener("resize", this.resetTableHeight);
    if (this.resizeObserver) {
      this.resizeObserver.disconnect();
      this.resizeObserver = null;
    }
  },
  methods: {
    handleSizeChange(size) {
      this.$emit("handleSizeChange", size);
    },
    handlePageChange(page) {
      this.$emit("handleCurrentChange", page);
    },
    resetTableHeight() {
      if (!this.autoHeight || this.height != null || this.maxHeight != null) {
        return;
      }
      const table = this.$refs.elTable;
      if (!table || !table.$el) return;
      // 表格顶部到视口的真实距离
      const top = table.$el.getBoundingClientRect().top;
      const h = window.innerHeight - top - 70; // 70 为底部留白（分页器等）
      this.innerHeight = h > 200 ? h : 500;
    },
    // —— 代理 el-table 常用方法，外部 this.$refs.table 直接调用 ——
    doLayout() {
      this.$refs.elTable && this.$refs.elTable.doLayout();
    },
    clearSelection() {
      this.$refs.elTable && this.$refs.elTable.clearSelection();
    },
    toggleRowSelection(row, selected) {
      this.$refs.elTable &&
        this.$refs.elTable.toggleRowSelection(row, selected);
    },
    toggleRowExpansion(row, expanded) {
      this.$refs.elTable &&
        this.$refs.elTable.toggleRowExpansion(row, expanded);
    },
    setCurrentRow(row) {
      this.$refs.elTable && this.$refs.elTable.setCurrentRow(row);
    },
    clearSort() {
      this.$refs.elTable && this.$refs.elTable.clearSort();
    },
    clearFilter(columnKey) {
      this.$refs.elTable && this.$refs.elTable.clearFilter(columnKey);
    },
    sort(prop, order) {
      this.$refs.elTable && this.$refs.elTable.sort(prop, order);
    },
  },
};
</script>

<style scoped lang="scss">
.table-plus {
  display: flex;
  flex-direction: column;

  &__pagination {
    display: flex;
    justify-content: flex-end;
    margin-top: 16px;
  }
}
.table-plus__pagination{
  padding-right: 30px;
}
</style>
